import type { Point, Size, WatermarkOptions } from './types'
import { getPixelRatio, getRotatePos, measureTextSize, toFixedNumber } from './utils'

export type DrawWatermarkOptions = Required<
  Pick<
    WatermarkOptions,
    | 'fontSize'
    | 'lineHeight'
    | 'textAlign'
    | 'fontFamily'
    | 'fontStyle'
    | 'fontWeight'
    | 'width'
    | 'height'
    | 'color'
    | 'content'
    | 'rotate'
  >
> & {
  gapX: number
  gapY: number
}

export interface DrawWatermarkResult {
  base64Url: string
  size: Size
}

function createCanvasAndContext(
  width: number,
  height: number,
  ratio = 1,
): [HTMLCanvasElement, CanvasRenderingContext2D, Size] {
  const canvas = document.createElement('canvas')

  const realWidth = width * ratio
  const realHeight = height * ratio

  canvas.width = realWidth
  canvas.height = realHeight

  const context = canvas.getContext('2d')!

  context.scale(ratio, ratio)

  return [canvas, context, { width: realWidth, height: realHeight }]
}

export function drawWatermark({
  color,
  content,
  fontFamily,
  fontSize,
  fontStyle,
  fontWeight,
  gapX,
  gapY,
  height,
  lineHeight,
  rotate,
  textAlign,
  width,
}: DrawWatermarkOptions): DrawWatermarkResult {
  const tmp = document.createElement('canvas')
  const tmpCtx = tmp.getContext('2d')!

  tmpCtx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`
  tmpCtx.textBaseline = 'bottom'

  let maxLineWidth = Number.MIN_VALUE
  // line-height: normal 大约是 1.2
  // https://developer.mozilla.org/en-US/docs/Web/CSS/line-height#normal
  let maxLineHeight = Math.floor(fontSize * 1.2)

  const lines: (Size & { text: string })[] = []

  for (const line of content.split(/\r?\n/)) {
    const { height: textHeight, width: textWidth } = measureTextSize(line, tmpCtx)

    maxLineWidth = Math.max(maxLineWidth, textWidth)
    maxLineHeight = Math.max(maxLineHeight, textHeight)

    lines.push({ text: line, width: textWidth, height: textHeight })
  }

  if (lineHeight === 'auto') {
    lineHeight = Math.ceil(maxLineHeight)
  }

  if (width === 'auto') {
    width = Math.ceil(maxLineWidth)
  }

  if (height === 'auto') {
    height = Math.ceil(lineHeight * lines.length)
  }

  if (width <= 0 || height <= 0) {
    return { base64Url: '', size: { width: 0, height: 0 } }
  }

  // eslint-disable-next-line @typescript-eslint/prefer-string-starts-ends-with
  const webpSupported = tmp.toDataURL('image/webp').indexOf('data:image/webp') === 0

  // 默认渲染 2 倍大小，避免模糊
  const ratio = getPixelRatio(tmpCtx) * 2

  // text canvas
  const [canvas, ctx, canvasSize] = createCanvasAndContext(width, height, ratio)

  ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px/${lineHeight}px ${fontFamily}`
  ctx.fillStyle = color
  ctx.textBaseline = 'bottom'

  // eslint-disable-next-line unicorn/no-for-loop
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    let alignOffset: number

    switch (textAlign) {
      case 'center': {
        alignOffset = (width - line.width) / 2

        break
      }
      case 'right': {
        alignOffset = width - line.width

        break
      }
      default: {
        alignOffset = 0

        break
      }
    }

    ctx.fillText(line.text, alignOffset, lineHeight * (i + 1))
  }

  // 旋转画布是正方形，直接使用画布大小
  // 为了避免裁剪的时候出现边缘问题，所以将画布大小设置为最大边长 + 1
  const rSize = Math.max(canvasSize.width, canvasSize.height) + 1

  const [rCanvas, rCtx, rCanvasSize] = createCanvasAndContext(rSize, rSize)

  const rTranslateX = Math.round(rSize / 2)
  const rTranslateY = Math.round(rSize / 2)

  // 将绘制的内容放到中心
  rCtx.translate(rTranslateX, rTranslateY)

  // 获取旋转角度
  const rotateAngle = toFixedNumber((Math.PI / 180) * rotate, 2)

  rCtx.rotate(rotateAngle)

  const canvasWidthCenter = canvasSize.width / 2
  const canvasHeightCenter = canvasSize.height / 2

  rCtx.drawImage(
    canvas,
    -canvasWidthCenter,
    -canvasHeightCenter,
    canvasSize.width,
    canvasSize.height,
  )

  // 由于下面需要将图片裁剪，所以需要将图片的边界计算出来
  const points: Point[] = [
    [-canvasWidthCenter, -canvasHeightCenter],
    [canvasWidthCenter, -canvasHeightCenter],
    [canvasWidthCenter, canvasHeightCenter],
    [-canvasWidthCenter, canvasHeightCenter],
  ]

  let left = Number.MAX_VALUE
  let right = Number.MIN_VALUE
  let top = Number.MAX_VALUE
  let bottom = Number.MIN_VALUE

  for (const [x, y] of points) {
    const [targetX, targetY] = getRotatePos(x, y, rotateAngle)

    left = Math.min(left, targetX)
    right = Math.max(right, targetX)
    top = Math.min(top, targetY)
    bottom = Math.max(bottom, targetY)
  }

  const cutLeft = left + rCanvasSize.width / 2
  const cutTop = top + rCanvasSize.height / 2
  const cutWidth = right - left
  const cutHeight = bottom - top

  // ================ Fill Alternate ================
  const realGapX = gapX * ratio
  const realGapY = gapY * ratio

  const filledWidth = (cutWidth + realGapX) * 2
  const filledHeight = cutHeight + realGapY

  // fill canvas
  const [fCanvas, fCtx, fCanvasSize] = createCanvasAndContext(filledWidth, filledHeight)

  const halfCutHeight = toFixedNumber(cutHeight / 2, 1)
  const distanceY = toFixedNumber(realGapY / 2, 1)

  const fPoints: Point[] = [
    [0, 0],
    [cutWidth + realGapX, -(halfCutHeight + distanceY)],
    [cutWidth + realGapX, halfCutHeight + distanceY],
  ]

  for (const [x, y] of fPoints) {
    fCtx.drawImage(rCanvas, cutLeft, cutTop, cutWidth, cutHeight, x, y, cutWidth, cutHeight)
  }

  return {
    base64Url: webpSupported
      ? fCanvas.toDataURL('image/webp', 0.8)
      : fCanvas.toDataURL('image/png'),
    size: {
      width: Math.floor(fCanvasSize.width / ratio),
      height: Math.floor(fCanvasSize.height / ratio),
    },
  }
}
